/**
 * DashboardPage — displayed at /app when no project is active.
 *
 * Shows the list of user projects with a click-to-navigate pattern
 * (navigates to /app/project/:id on selection) and provides a simple
 * project creation form.
 *
 * Reads the project list from Zustand (populated by AppShell on login).
 * Uses useOutletContext to access AppShell's showToast utility.
 */
import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useStore } from '../../store/useStore';
import { createProject } from '../../api/projects';
import { isErrorResponse } from '../../utils/apiUtils';
import { useProjectLimit } from '../../pricing/useProjectLimit';
import { AppShellContext } from './AppShell';
import { ProjectModel } from '../../types';
import CollapsibleHelpItem, { HelpItem } from '../ui/CollapsibleHelpItem';

/**
 * The main dashboard shown after login when no project URL is active.
 * Lists all projects and lets the user navigate into one or create a new one.
 */
export default function DashboardPage() {
    const navigate = useNavigate();
    const { showToast, projectsLoading } = useOutletContext<AppShellContext>();

    const { user, projects, setProjects } = useStore();
    const { ensureCanCreateProject } = useProjectLimit();

    const [newProjectName, setNewProjectName] = useState('');
    const [creating, setCreating] = useState(false);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    /** Navigate to a project's canvas page. */
    const handleSelectProject = (projectId: string) => {
        navigate(`/app/project/${projectId}`);
    };

    /** Create a new project and navigate to it. */
    const handleCreateProject = async () => {
        const trimmedName = newProjectName.trim();
        if (!trimmedName || !user) return;

        // Same project-limit guard AppShell applies — see pricing/useProjectLimit.
        if (!ensureCanCreateProject()) return;

        setCreating(true);
        const result = await createProject(trimmedName);
        setCreating(false);

        if (isErrorResponse(result)) {
            showToast(result.error);
            return;
        }

        const project = result as ProjectModel;
        setProjects([...projects, project]);
        setNewProjectName('');
        navigate(`/app/project/${project._id}`);
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className='dashboard-page'>

            <div className='dashboard-layout'>
                <div className='dashboard-column dashboard-column--left'>
                    <div className='dashboard-column-header'>
                        <h1 className='dashboard-column-title'>Projects</h1>
                    </div>
                    <div className='dashboard-projects-card'>
                        {/* ── Create new project form — always at top of card ─────── */}
                        <div className='dashboard-create-form'>
                            <input
                                className='dashboard-create-input'
                                type='text'
                                placeholder='New project name…'
                                value={newProjectName}
                                onChange={(e) => setNewProjectName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCreateProject();
                                }}
                                aria-label='New project name'
                            />
                            <button
                                className='app-cta-btn'
                                onClick={handleCreateProject}
                                disabled={creating || !newProjectName.trim()}
                                aria-label='Create project'
                            >
                                {creating ? 'Creating…' : 'Create'}
                            </button>
                        </div>

                        {/* ── Project list ─────────────────────────────────────────── */}
                        {projectsLoading ? (
                            <div className='dashboard-loading'>
                                <div className='dashboard-loading-spinner' aria-hidden='true' />
                                <p className='dashboard-loading-text'>Loading projects…</p>
                            </div>
                        ) : projects.length === 0 ? (
                            <div className='dashboard-empty'>
                                <p className='dashboard-empty-title'>No projects yet</p>
                                <p className='dashboard-empty-hint'>
                                    Create your first project above to start building your opportunity solution tree.
                                </p>
                            </div>
                        ) : (
                            <ul className='dashboard-project-list'>
                                {projects.map((project) => (
                                    <li key={project._id} className='dashboard-project-item'>
                                        <button
                                            className='dashboard-project-btn'
                                            onClick={() => handleSelectProject(project._id)}
                                        >
                                            <span className='dashboard-project-name'>{project.name}</span>
                                            <span className='dashboard-project-arrow' aria-hidden='true' />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className='dashboard-column dashboard-column--middle'>
                    <div className='dashboard-column-header'>
                        <h2 className='dashboard-column-title'>Help & Onboarding</h2>
                    </div>
                    <div className='dashboard-help-card'>
                        <div className='dashboard-help-intro'>
                            {/* <p className='dashboard-help-text'>
                                Welcome to Zeytune! Here are a few quick guides and tips to help you get started.
                            </p> */}
                        </div>

                        <div className='dashboard-help-items'>
                            {/* Define help items as data so new items can be added easily */}
                            {(
                                [
                                    {
                                        id: 'getting-started',
                                        title: 'Getting started -- Understanding projects',
                                        content: (
                                            <div>
                                                <p>
                                                    Zeytune organizes your work into projects. Each project can have several <b>Opportunity Solution Trees</b>, and <b>User Insights</b>. You have the flexibility to create new projects either from here on the left Project Dashboard panel, or from the dropdown (folder icon) when you are inside your project.
                                                </p>
                                                <p>
                                                    Any changes you make inside your project are instantly saved to the Zeytune database. You also have the option to export your project as a <b>.zey</b> backup file locally onto your machine. This can be done from the project configuration dropdown (folder icon) from within your project.
                                                </p>
                                                <img src='/onboarding/zeytune-newprojectdropdown.gif' alt='example' className="dashboard-thumb" />
                                            </div>
                                        ),
                                    },
                                    {
                                        id: 'templates',
                                        title: 'Opportunity Solution Trees',
                                        content: (
                                            <div>
                                                <p>Zeytune's core is built on the concept of <b>Opportunity Solution Trees</b>, a framework originally developed by the widely acclaimed product discovery coach, Teresa Torres. These trees provide teams with a structured way to go about their product discovery process, and ensuring that they conduct the critically important initial discovery, research, validation based on customer interviews and insights.</p>
                                                <p>Zeytune is built in a way that it makes this experience interactively, simple, and contextually relevant. As the solutions and experiments that a team works on are derived from opportunities and the core outcome at the top, they have already been fed with user insights and research data that the product team injects into the top part of the tree.</p>
                                                <img src='/onboarding/ost-intro1.png' alt='example' className="dashboard-thumb" />
                                                <p></p>
                                                <h4>Outcomes</h4>
                                                <p>An outcome is the top node of the an OST, and is essentially the goal you want to achieve. It should ideally be something measurable and derived from business goals.</p>
                                                <h4>Opportunities</h4>
                                                <p>These are customer pain points, wishes, areas requiring improvement, etc. An opportunity can have sub-opportunities, but nesting too many opportunities can also signal over-analysis and complication. Opportunities can easily be sliced in Zeytune using AI based slicing or by manually entering the slices.</p>
                                                <h4>Solutions</h4>
                                                <p>A solution is an idea from members of the team to help solve the opportunity under which it is created. Some teams like to map this to Epics, with the opportunities serving as Themes.</p>
                                                <h4>Experiments / Stories</h4>
                                                <p>These are the last nodes on the tree, and serve to validate the solution. You can configure the project either let you create Experiments or Stories under Solution nodes, each having their own input structure.</p>
                                            </div>
                                        ),
                                    },
                                    {
                                        id: 'creating-nodes',
                                        title: 'Creating Nodes',
                                        content: (
                                            <div>
                                                <p>Creating a sub-node from an existing node requires first clicking the node, then clicking the "+" icon. This opens up a custom modal for each node type in which the information needs to be filled in to ensure information completeness.</p>
                                                <img src='/onboarding/zeytune-creating-nodes-1.gif' alt='example' className="dashboard-thumb" />
                                                <p>Also, clicking on an existing node opens up the node property panel which can be easily moved around the scene to better organize your workspace.</p>
                                            </div>
                                        ),
                                    },
                                    {
                                        id: 'slicing-nodes',
                                        title: 'Slicing Nodes (Opportunities / Stories)',
                                        content: (
                                            <div>
                                                <p>The purpose of slicing opportunity or story nodes is to break down large, complex items into smaller manageable ones. This can be done by clicking the slash symbol (<b>/</b>) that only appears when either an opportunity or story node is clicked.</p>
                                                <p>Pro accounts can also use AI based slicing by clicking the button in the slice modal.</p>
                                                <img src='/onboarding/zeytune-slicing-nodes-3.gif' alt='example' className="dashboard-thumb" />
                                            </div>
                                        ),
                                    },
                                    {
                                        id: 'connecting-adjacent-branches',
                                        title: 'Connecting Adjacent Branches',
                                        content: (
                                            <div>
                                                <p>Sometimes there can be nodes in different branches that can have something in common which needs to be identified and tracked somehow. Instead of having to manually add references, using move your mouse over to the source node, press the Ctrl key to show the pinch icon, and then click drag over to the target node to establish a connection. Opening either node will show the relationship defined between them.</p>
                                                <p>This helps in build a 'soft' connection between nodes, so that AI based slicing or other generation workflows can use these connections as a reference.</p>
                                                <img src='/onboarding/zeytune-connecting-nodes-3.gif' alt='example' className="dashboard-thumb" />
                                            </div>
                                        ),
                                    },
                                    {
                                        id: 'creating-linking-insights',
                                        title: 'Creating and Linking User Insights [WIP]',
                                        content: (
                                            <div>
                                                <p>User Insights can be added in the panel on the left. It is important to have description inside the insight which can be connected to any existing opportunities using the pinch-drag method, as shown in the video below. An Insight card can be editing again by double-clicking its header.</p>
                                                <p>It's important to know that Insights are accessible across all the trees inside a project. Which means that the left panel will always show all the insights in a project.</p>
                                                <img src='/onboarding/zeytune-connecting-insights-3.gif' alt='example' className="dashboard-thumb" />
                                            </div>
                                        ),
                                    },
                                ] as HelpItem[]
                            ).map((it) => (
                                <CollapsibleHelpItem key={it.id} item={it} />
                            ))}
                        </div>
                    </div>
                </div>

                <div className='dashboard-column dashboard-column--right'>
                    <div className='dashboard-column-header'>
                        <h2 className='dashboard-column-title'>Latest Updates</h2>
                    </div>
                    <div className='dashboard-updates-card'>
                        <ul className='dashboard-updates-list'>
                            <li className='dashboard-updates-item'>
                                <div className='update-title'>Improved outcome analytics and status monitoring</div>
                                <div className='update-meta'>Jul 15</div>
                            </li>
                            <li className='dashboard-updates-item'>
                                <div className='update-title'>New AI-based insights management</div>
                                <div className='update-meta'>Jun 15</div>
                            </li>
                            <li className='dashboard-updates-item'>
                                <div className='update-title'>Project dashboard overhaul, and onboarding addition</div>
                                <div className='update-meta'>Apr 20</div>
                            </li>
                            <li className='dashboard-updates-item'>
                                <div className='update-title'>AWS backend setup for AI workflows</div>
                                <div className='update-meta'>Apr 15</div>
                            </li>
                            <li className='dashboard-updates-item'>
                                <div className='update-title'>Bug fixes & performance improvements</div>
                                <div className='update-meta'>Apr 10</div>
                            </li>
                            <li className='dashboard-updates-item'>
                                <div className='update-title'>Multiple bug fixes and performance improvements</div>
                                <div className='update-meta'>Mar 28</div>
                            </li>
                            <li className='dashboard-updates-item'>
                                <div className='update-title'>Node connectivity implementation</div>
                                <div className='update-meta'>Mar 15</div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
